import React, { useState, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import AttachedDocumentsFormDialog from '../detailDialogForms/AttachedDocumentsFormDialog';
import { useGetWarrantyClaimDocumentsByIdQuery, useDeleteWarrantyClaimDocumentMutation, useGetWarrantyClaimsByIdQuery } from '../../../../../../../api/endpoints/warrantyClaims';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../../../utils/dateUtils';
import { usePermissions } from '../../../../../../../hooks/usePermissions';
import toastService from '../../../../../../../utils/toastService';
import { WarrantyClaimStatus } from '../../../../../../../utils/warrantyClaimsEnums';
import { getEnv } from '../../../../../../../config/runtimeConfig';

const DISABLED_STATUSES = [WarrantyClaimStatus.RETURNED_TO_KARSAN, WarrantyClaimStatus.ACCEPTED, WarrantyClaimStatus.REJECTED];

const AttachedDocumentsTable = ({ claimId }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingDocument, setEditingDocument] = useState(null);

    const [lazyParams, setLazyParams] = useState({
        rows: 10,
        sortField: null,
        sortOrder: null
    });

    const { data: documentsList, isLoading, refetch } = useGetWarrantyClaimDocumentsByIdQuery(claimId);

    const { data: warrantyClaimGetById } = useGetWarrantyClaimsByIdQuery({ id: claimId, languageId: getCurrentLanguageId() });

    const [deleteDocument] = useDeleteWarrantyClaimDocumentMutation();

    const disabledStatuses = DISABLED_STATUSES.includes(warrantyClaimGetById?.status);
    const disabledForRole = (hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole()) && warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING;

    useEffect(() => {
        if (claimId) {
            refetch();
        }
    }, [claimId, refetch]);

    const sortedData = useMemo(() => {
        const allItems = documentsList?.data || [];

        if (!lazyParams.sortField || lazyParams.sortOrder === 0) {
            return allItems;
        }

        return [...allItems].sort((a, b) => {
            let aValue = a[lazyParams.sortField];
            let bValue = b[lazyParams.sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return lazyParams.sortOrder;
            if (bValue == null) return -lazyParams.sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return lazyParams.sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -lazyParams.sortOrder;
            if (aValue > bValue) return lazyParams.sortOrder;
            return 0;
        });
    }, [documentsList?.data, lazyParams.sortField, lazyParams.sortOrder]);

    const handleNewDocument = () => {
        setEditingDocument(null);
        setDialogVisible(true);
    };

    const handleDownloadAll = async () => {
        if (!sortedData || sortedData.length === 0) {
            toastService.warn(th('warrantyClaims.detail.documents.noDocuments'));
            return;
        }

        try {
            // Base URL'den trailing slash'i kaldır
            const baseUrl = getEnv('REACT_APP_API_URL')?.replace(/\/$/, '') || '';
            const url = `${baseUrl}/v1/warrantyclaimsdocument/downloadalldocuments?WarrantyClaimId=${claimId}`;
            
            // Dil bilgisini localStorage'dan al
            let locale = 'TR';
            try {
                const savedLanguage = localStorage.getItem('selectedLanguage');
                if (savedLanguage) {
                    const languageData = JSON.parse(savedLanguage);
                    const localeMap = {
                        TR: 'TR', US: 'US', DE: 'DE',
                        FR: 'FR', IT: 'IT', RO: 'RO'
                    };
                    locale = localeMap[languageData.code] || 'TR';
                }
            } catch (e) {
                // Dil ayarı alınamazsa varsayılan TR kullan
            }

            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Accept': 'application/zip, application/octet-stream',
                    'Accept-Language': locale
                }
            });

            if (!response.ok) {
                // Hata durumunda response body'yi oku
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || errorData.errorMessage || 'Download failed');
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            
            if (blob.size === 0) {
                toastService.warn(th('warrantyClaims.detail.documents.noDocuments'));
                return;
            }
            
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            
            // Dosya adını header'dan al veya default kullan
            const contentDisposition = response.headers.get('content-disposition');
            let fileName = `WarrantyClaim_${claimId}_Documents.zip`;
            if (contentDisposition) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
                if (matches != null && matches[1]) {
                    fileName = matches[1].replace(/['"]/g, '');
                }
            }
            
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            
            toastService.success(th('warrantyClaims.detail.documents.downloadSuccess'));
        } catch (error) {
            console.error('Dosyalar indirilirken hata:', error);
            toastService.error(th('warrantyClaims.detail.documents.downloadError'));
        }
    };

    const handleDelete = async (documentId) => {
        try {
            const result = await deleteDocument({
                id: claimId,
                documentId: documentId
            }).unwrap();
            if (result) {
                refetch();
                setDialogVisible(false);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Doküman silinirken hata:', error);
        }
    };

    const handleEdit = (rowData) => {
        setEditingDocument(rowData);
        setTimeout(() => {
            setDialogVisible(true);
        }, 0);
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2 justify-content-center">
            <i disabled={disabledStatuses} onClick={() => handleEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.detail.documents.edit')}></i>
        </div>
    );

    const fileNameTemplate = (rowData) => {
        // Dosya adından sadece gerçek dosya adını al
        const fullPath = rowData.fileName;
        const fileName = fullPath.split('/').pop(); // Son kısmı al
        return fileName || fullPath;
    };

    return (
        <div>
            <div className="flex justify-content-between align-items-center mb-3 mt-3">
                <Button label={th('warrantyClaims.detail.documents.addNew')} icon="pi pi-plus" onClick={handleNewDocument} disabled={disabledStatuses || disabledForRole} />
                <Button 
                    label={th('warrantyClaims.detail.documents.downloadAll')} 
                    icon="pi pi-download" 
                    onClick={handleDownloadAll} 
                    disabled={!sortedData || sortedData.length === 0}
                    severity="info"
                    outlined
                />
            </div>
            <DataTable
                value={sortedData}
                className="p-datatable-gridlines"
                showGridlines
                paginator
                rows={lazyParams.rows}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                dataKey="id"
                loading={isLoading}
                emptyMessage={th('warrantyClaims.detail.documents.emptyMessage')}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '60rem' }}
            >
                <Column body={actionBodyTemplate} style={{ width: '5%', minWidth: '33px' }} bodyClassName="text-center" headerClassName="text-center" exportable={false} />
                <Column field="fileName" header={th('warrantyClaims.detail.documents.fileName')} sortable body={fileNameTemplate} />
                <Column field="description" header={th('warrantyClaims.detail.documents.description')} sortable />
                <Column field="subModuleName" header={th('warrantyClaims.detail.documents.documentType')} sortable />
                <Column field="subReferenceTitle" header={th('warrantyClaims.detail.documents.partCode')} sortable />
                <Column field="fileType" header={th('warrantyClaims.detail.documents.fileType')} sortable />
                <Column body={(row) => formatDateOnly(row?.createdDate)} field="createdDate" header={th('warrantyClaims.detail.documents.createdDate')} sortable />
            </DataTable>

            <AttachedDocumentsFormDialog
                disabledStatuses={disabledStatuses || disabledForRole}
                claimId={claimId}
                dialogVisible={dialogVisible}
                hideDialog={() => setDialogVisible(false)}
                onDelete={handleDelete}
                editingDocument={editingDocument}
                onUpdateSuccess={() => {
                    // Update işleminden sonra liste verilerini yenile
                    refetch();
                }}
            />
        </div>
    );
};

export default AttachedDocumentsTable;
